import { useQuery } from '@tanstack/react-query'

import { ATSCandidate } from '@pkg/types/api'

import api from '@/api'

export const ATS_CANDIDATES_KEY = 'ats-candidates'

const MINUTE = 1000 * 60

export interface UseSearchATSCandidatesOptions {
  enabled?: boolean
  staleTime?: number
}

/**
 * Hook to search for candidates from the user's connected ATS using the ATS
 * @param query - The search query string
 * @param options - Query options including enabled state and stale time
 * @returns Query result with candidates data
 */
export const useSearchATSCandidates = (
  query: string,
  options: UseSearchATSCandidatesOptions = {},
) => {
  const { enabled = true, staleTime = 2 * MINUTE } = options

  return useQuery({
    queryKey: [ATS_CANDIDATES_KEY, 'search', query],
    queryFn: async (): Promise<ATSCandidate[]> => {
      if (!query.trim()) {
        return []
      }

      const response = await api.searchATSCandidates(query.trim())
      return response.data.candidates
    },
    enabled: enabled && !!query.trim(),
    staleTime,
    retry: 2,
    retryDelay: 1000,
  })
}

/**
 * Helper function to get the full name of a ATS candidate
 * @param candidate - The ATS candidate object
 * @returns The full name or a fallback string
 */
export const getATSCandidateFullName = (candidate: ATSCandidate): string => {
  if (candidate.firstName && candidate.lastName) {
    return `${candidate.firstName} ${candidate.lastName}`
  }
  if (candidate.firstName) {
    return candidate.firstName
  }
  if (candidate.lastName) {
    return candidate.lastName
  }
  return 'Unknown Candidate'
}

/**
 * Helper function to get the primary email of a ATS candidate
 * @param candidate - The ATS candidate object
 * @returns The primary email or null if not available
 */
export const getATSCandidatePrimaryEmail = (
  candidate: ATSCandidate,
): string | null => {
  if (!candidate.emailAddresses || candidate.emailAddresses.length === 0) {
    return null
  }

  // Return the first email address
  return candidate.emailAddresses[0]?.value || null
}

/**
 * Helper function to get all email addresses of a ATS candidate
 * @param candidate - The ATS candidate object
 * @returns Array of email addresses
 */
export const getATSCandidateEmails = (candidate: ATSCandidate): string[] => {
  if (!candidate.emailAddresses) {
    return []
  }

  return candidate.emailAddresses
    .map((email) => email.value)
    .filter(Boolean) as string[]
}

/**
 * Helper function to check if a ATS candidate has a valid email
 * @param candidate - The ATS candidate object
 * @returns True if the candidate has at least one email address
 */
export const hasATSCandidateEmail = (candidate: ATSCandidate): boolean => {
  return getATSCandidateEmails(candidate).length > 0
}
